import { CircularProgress, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  Inline,
  Stack,
  useSnackbarStack,
  VisibilityObserver,
} from '@superdispatch/ui';
import { Alert, Box } from '@superdispatch/ui-lab';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { updateSearchQuery } from 'shared/utils/URLUtils';
import { useUnratedShippers } from './data/CarrierRatingsAPI';
import { RatingIllustration } from './RatingIllustration';
import { UnratedShipper } from './UnratedShipper';
import { Info } from '@material-ui/icons';
import { ShipperRatingDrawer } from './ShipperRatingDrawer';

const isLoadboardMobile = import.meta.env.VITE_APP_NAME === 'slbm';

interface CarrierRatingsToGiveProps {
  dense?: boolean;
  onRateShipper?: (guid: string) => void;
}

export function CarrierRatingsToGive({
  dense = false,
  onRateShipper,
}: CarrierRatingsToGiveProps) {
  const { addSnackbar } = useSnackbarStack();
  const navigate = useNavigate();
  const location = useLocation();
  const [_query, setQuery] = useQuery();

  const { data: carrierSettings } = useCarrierSettings();
  const isRateAllowed =
    carrierSettings?.carrier.verification_status === 'approved';

  const { data, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useUnratedShippers();

  const unratedShippers = useMemo(() => {
    return data?.pages.flatMap((page) => page.data);
  }, [data]);

  const [currentShipperGuid, setCurrentShipperGuid] = useState<string>();

  useEffect(() => {
    if (error) {
      addSnackbar('Failed to fetch ratings to give', { variant: 'error' });
    }
  }, [addSnackbar, error]);

  const onClickRate = (guid: string) => {
    if (isLoadboardMobile) {
      const search = updateSearchQuery(location.search, (searchParams) => ({
        ...searchParams,
        utm_medium: 'Ratings To Give',
      }));

      navigate({ pathname: `/rate-shipper/${guid}/`, search });
    } else {
      setQuery({ utm_medium: 'Ratings To Give' }); // for analytics
      setCurrentShipperGuid(guid);
      onRateShipper?.(guid);
    }
  };

  return (
    <>
      {currentShipperGuid && (
        <ShipperRatingDrawer
          shipperGUID={currentShipperGuid}
          open={!!currentShipperGuid}
          onClose={() => {
            setQuery({ utm_medium: undefined });
            setCurrentShipperGuid(undefined);
          }}
        />
      )}

      {data ? (
        unratedShippers?.length ? (
          <Box width="100%" paddingTop="xsmall">
            <Stack space="large" align="center">
              <Stack space="small">
                {isRateAllowed && (
                  <Inline verticalAlign="center" space="xsmall">
                    <Info fontSize="small" color="action" />
                    <Typography color="textSecondary">
                      You can give ratings within 90 days of delivery or
                      cancelation.
                    </Typography>
                  </Inline>
                )}
                {!isRateAllowed && (
                  <Alert severity="critical">
                    <Inline verticalAlign="center">
                      <span>Only verified carriers can leave ratings.</span>
                    </Inline>
                  </Alert>
                )}
                {unratedShippers.map((unratedShipper) => (
                  <UnratedShipper
                    key={unratedShipper.guid}
                    rateButtonText={dense ? 'Rate' : 'Rate Shipper'}
                    unratedShipper={unratedShipper}
                    onClickRate={() => {
                      onClickRate(unratedShipper.guid);
                    }}
                  />
                ))}
              </Stack>

              {hasNextPage && (
                <VisibilityObserver
                  onChange={(visibility) => {
                    if (!isFetchingNextPage && visibility === 'visible') {
                      void fetchNextPage();
                    }
                  }}
                  render={({ ref }) => (
                    <Box width="100%" ref={ref}>
                      {Array.from({ length: 3 }, (_, key) => (
                        <Skeleton
                          key={key}
                          width="100%"
                          height="50px"
                          animation={false}
                        />
                      ))}
                    </Box>
                  )}
                />
              )}
            </Stack>
          </Box>
        ) : (
          <Stack align="center">
            <Box
              width="90px"
              height="80px"
              marginTop={['small']}
              marginBottom="xsmall"
            >
              <RatingIllustration />
            </Box>

            <Typography variant="h3" color="textSecondary" align="center">
              All caught up!
            </Typography>

            <Typography color="textSecondary" align="center">
              Shippers you haven’t rated <br />
              yet will be listed here.
            </Typography>
          </Stack>
        )
      ) : (
        <Box width="40px" height="172px" margin="auto" paddingTop="xxlarge">
          <CircularProgress />
        </Box>
      )}
    </>
  );
}
